/**
 * Pure geometry for the quadrant chart: 0–1 model coordinates in, SVG
 * coordinates out. No React or renderer assumptions live here.
 */

import {
  AXIS_LABEL_GAP,
  CORNER_LABEL_INSET,
  LABEL_EDGE_PADDING,
  MARGIN_BOTTOM,
  MARGIN_LEFT,
  MARGIN_RIGHT,
  MARGIN_TOP,
  NO_ROTATION,
  POINT_LABEL_BASELINE_OFFSET,
  POINT_LABEL_GAP,
  VIEW_HEIGHT,
  VIEW_WIDTH,
  Y_AXIS_LABEL_ROTATION,
} from './quadrant-constants';
import {
  QuadrantCorner,
  QuadrantLabelAnchor,
  type ParsedQuadrant,
  type QuadrantAxis,
  type QuadrantAxisLabel,
  type QuadrantCornerLabel,
  type QuadrantLayout,
  type QuadrantPlottedPoint,
  type QuadrantPoint,
} from './quadrant-types';

const PLOT_LEFT = MARGIN_LEFT;
const PLOT_TOP = MARGIN_TOP;
const PLOT_WIDTH = VIEW_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
const PLOT_HEIGHT = VIEW_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;
const PLOT_RIGHT = PLOT_LEFT + PLOT_WIDTH;
const PLOT_BOTTOM = PLOT_TOP + PLOT_HEIGHT;
const MID_X = PLOT_LEFT + PLOT_WIDTH / 2;
const MID_Y = PLOT_TOP + PLOT_HEIGHT / 2;
const AXIS_MIDPOINT = 0.5;

interface CornerPlacement {
  x: number;
  y: number;
  anchor: QuadrantLabelAnchor;
}

const CORNER_PLACEMENTS: Record<QuadrantCorner, CornerPlacement> = {
  [QuadrantCorner.TopRight]: {
    x: PLOT_RIGHT - CORNER_LABEL_INSET,
    y: PLOT_TOP + CORNER_LABEL_INSET,
    anchor: QuadrantLabelAnchor.End,
  },
  [QuadrantCorner.TopLeft]: {
    x: PLOT_LEFT + CORNER_LABEL_INSET,
    y: PLOT_TOP + CORNER_LABEL_INSET,
    anchor: QuadrantLabelAnchor.Start,
  },
  [QuadrantCorner.BottomLeft]: {
    x: PLOT_LEFT + CORNER_LABEL_INSET,
    y: PLOT_BOTTOM - CORNER_LABEL_INSET / 2,
    anchor: QuadrantLabelAnchor.Start,
  },
  [QuadrantCorner.BottomRight]: {
    x: PLOT_RIGHT - CORNER_LABEL_INSET,
    y: PLOT_BOTTOM - CORNER_LABEL_INSET / 2,
    anchor: QuadrantLabelAnchor.End,
  },
};

const CORNER_ORDER: QuadrantCorner[] = [
  QuadrantCorner.TopLeft,
  QuadrantCorner.TopRight,
  QuadrantCorner.BottomLeft,
  QuadrantCorner.BottomRight,
];

function clamp(input: { value: number; min: number; max: number }): number {
  return Math.min(input.max, Math.max(input.min, input.value));
}

function toCanvasX(x: number): number {
  return PLOT_LEFT + x * PLOT_WIDTH;
}

function toCanvasY(y: number): number {
  // Model y grows upward; SVG y grows downward.
  return PLOT_BOTTOM - y * PLOT_HEIGHT;
}

/**
 * Labels flip to the inside of the box past the midline, so a point at x=1
 * writes leftward instead of running off the right edge.
 */
function selectLabelAnchor(x: number): QuadrantLabelAnchor {
  return x > AXIS_MIDPOINT ? QuadrantLabelAnchor.End : QuadrantLabelAnchor.Start;
}

function plotPoint(input: { point: QuadrantPoint; index: number }): QuadrantPlottedPoint {
  const centerX = toCanvasX(input.point.x);
  const centerY = toCanvasY(input.point.y);
  const labelAnchor = selectLabelAnchor(input.point.x);
  const labelOffset =
    labelAnchor === QuadrantLabelAnchor.End ? -POINT_LABEL_GAP : POINT_LABEL_GAP;

  return {
    label: input.point.label,
    debugIdentity: `${input.index}-${input.point.label}`,
    centerX,
    centerY,
    labelX: clamp({
      value: centerX + labelOffset,
      min: PLOT_LEFT + LABEL_EDGE_PADDING,
      max: PLOT_RIGHT - LABEL_EDGE_PADDING,
    }),
    labelY: clamp({
      value: centerY + POINT_LABEL_BASELINE_OFFSET,
      min: PLOT_TOP + LABEL_EDGE_PADDING,
      max: PLOT_BOTTOM - LABEL_EDGE_PADDING / 2,
    }),
    labelAnchor,
  };
}

function buildCornerLabels(corners: ParsedQuadrant['corners']): QuadrantCornerLabel[] {
  return CORNER_ORDER.reduce<QuadrantCornerLabel[]>((labels, corner) => {
    const text = corners[corner];
    if (!text) {
      return labels;
    }

    const placement = CORNER_PLACEMENTS[corner];

    return [
      ...labels,
      { text, x: placement.x, y: placement.y, anchor: placement.anchor },
    ];
  }, []);
}

function buildXAxisLabels(xAxis: QuadrantAxis | null): QuadrantAxisLabel[] {
  if (!xAxis) {
    return [];
  }

  const baselineY = PLOT_BOTTOM + AXIS_LABEL_GAP;
  const startLabel: QuadrantAxisLabel = {
    text: xAxis.start,
    x: PLOT_LEFT,
    y: baselineY,
    anchor: QuadrantLabelAnchor.Start,
    rotation: NO_ROTATION,
  };

  if (!xAxis.end) {
    return [startLabel];
  }

  return [
    startLabel,
    {
      text: xAxis.end,
      x: PLOT_RIGHT,
      y: baselineY,
      anchor: QuadrantLabelAnchor.End,
      rotation: NO_ROTATION,
    },
  ];
}

function buildYAxisLabels(yAxis: QuadrantAxis | null): QuadrantAxisLabel[] {
  if (!yAxis) {
    return [];
  }

  const baselineX = PLOT_LEFT - AXIS_LABEL_GAP;
  // Rotated -90°, so anchor Start reads from the bottom of the axis upward.
  const startLabel: QuadrantAxisLabel = {
    text: yAxis.start,
    x: baselineX,
    y: PLOT_BOTTOM,
    anchor: QuadrantLabelAnchor.Start,
    rotation: Y_AXIS_LABEL_ROTATION,
  };

  if (!yAxis.end) {
    return [startLabel];
  }

  return [
    startLabel,
    {
      text: yAxis.end,
      x: baselineX,
      y: PLOT_TOP,
      anchor: QuadrantLabelAnchor.End,
      rotation: Y_AXIS_LABEL_ROTATION,
    },
  ];
}

export function buildQuadrantLayout(parsed: ParsedQuadrant): QuadrantLayout {
  return {
    plotLeft: PLOT_LEFT,
    plotTop: PLOT_TOP,
    plotWidth: PLOT_WIDTH,
    plotHeight: PLOT_HEIGHT,
    midX: MID_X,
    midY: MID_Y,
    points: parsed.points.map((point, index) => plotPoint({ point, index })),
    cornerLabels: buildCornerLabels(parsed.corners),
    axisLabels: [...buildXAxisLabels(parsed.xAxis), ...buildYAxisLabels(parsed.yAxis)],
  };
}
